// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Investor Dashboard Page
// Route: /investor
// apps/web/src/routes/investor.tsx
//
// THE SHOWPIECE. This is what investors see. Maximum visual impact.
// Hero compression ratio, replacement cost accumulation, phase timeline,
// complexity breakdown, capital efficiency, benchmark comparison.
// Also used by the public /share/$token route via shared component.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  NeonValue,
  EngineTooltip,
  MetricCard,
  LoadingShimmer,
  StatusBadge,
  EmptyState,
} from '../components/engine/EnginePageShell';
import { useCounter } from '../components/engine/hooks';
import { useInvestorDashboard, useShareLinks, useShareLinkMutations } from '../lib/api/investor';
import { useProjects } from '../lib/api/projects';
import { useSubscription, normalizeTier, meetsMinTier } from '../lib/api/billing';
import type { InvestorDashboardData, PhaseTimelineEntry } from '../lib/api/investor';

export const Route = createFileRoute('/investor')({
  component: InvestorPage,
});

function InvestorPage() {
  const { data: projects } = useProjects();
  const { data: subscription } = useSubscription();
  const currentTier = normalizeTier(subscription?.tier);
  const hasDashboardAccess = meetsMinTier(currentTier, 'Professional');
  const hasShareLinkAccess = meetsMinTier(currentTier, 'Enterprise');
  const [projectId, setProjectId] = useState(0);
  const { data, loading, error } = useInvestorDashboard(hasDashboardAccess ? projectId : 0);
  const { data: shareLinks, refetch: refetchLinks } = useShareLinks(hasDashboardAccess ? (projectId || undefined) : undefined);
  const linkMutations = useShareLinkMutations();

  // Auto-select first project
  if (projectId === 0 && projects && projects.length > 0) {
    setProjectId(projects[0].id);
  }

  return (
    <EnginePageShell
      title="Investor Dashboard"
      subtitle="CAPITAL INTELLIGENCE"
      icon="📊"
      accentColor={C.amber}
      statusLabel="INVESTOR VIEW"
      statusColor={C.amber}
    >
      {/* Project selector + share controls */}
      <GlassPanel accent={C.amber} delay={0.1} style={{ marginBottom: 20, padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>PROJECT</span>
            <select
              value={projectId}
              onChange={e => setProjectId(Number(e.target.value))}
              style={{
                background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.faint}`, borderRadius: 6,
                padding: '6px 12px', color: C.text, fontFamily: F.display, fontSize: 13, outline: 'none',
              }}
            >
              <option value={0}>Select...</option>
              {projects?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              onClick={async () => {
                if (!projectId || !hasShareLinkAccess) return;
                const link = await linkMutations.create({ projectId, showReplacementCost: true, showContributors: true });
                if (link) { refetchLinks(); navigator.clipboard?.writeText(link.shareUrl); }
              }}
              disabled={!projectId || linkMutations.loading || !hasShareLinkAccess}
              title={!hasShareLinkAccess ? 'Share links require the Enterprise plan' : undefined}
              style={{
                background: `${C.neon}12`, border: `1px solid ${hasShareLinkAccess ? C.neon : C.dim}30`, color: hasShareLinkAccess ? C.neon : C.dim,
                fontFamily: F.mono, fontSize: 10, padding: '5px 14px', borderRadius: 6,
                cursor: projectId && hasShareLinkAccess ? 'pointer' : 'not-allowed', opacity: projectId && hasShareLinkAccess ? 1 : 0.4,
              }}
            >
              🔗 CREATE SHARE LINK
              {!hasShareLinkAccess && <span style={{ marginLeft: 4, fontSize: 8, color: C.amber }}>[ENTERPRISE]</span>}
            </button>
          </div>
        </div>

        {/* Active share links */}
        {shareLinks && shareLinks.filter(l => !l.isRevoked).length > 0 && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.faint}` }}>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>ACTIVE SHARE LINKS</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {shareLinks.filter(l => !l.isRevoked).map(link => (
                <div key={link.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px',
                  background: `${C.neon}08`, border: `1px solid ${C.neon}15`, borderRadius: 6,
                }}>
                  <span style={{ fontSize: 10, fontFamily: F.mono, color: C.neon }}>{link.shareUrl.split('/').pop()?.slice(0, 8)}...</span>
                  <span style={{ fontSize: 9, fontFamily: F.mono, color: C.dim }}>{link.accessCount} views</span>
                  <button onClick={async () => { await linkMutations.revoke(link.id); refetchLinks(); }}
                    style={{ background: 'none', border: 'none', color: C.coral, cursor: 'pointer', fontSize: 10, padding: 0 }}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </GlassPanel>

      {/* Dashboard content */}
      {!hasDashboardAccess ? (
        <GlassPanel accent={C.amber} delay={0.2} style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>📊</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>Investor Dashboard</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.6 }}>
            Board-ready compression ratios, replacement cost analysis, and shareable investor views.
          </div>
          <div style={{
            display: 'inline-block', fontSize: 10, fontFamily: F.mono, letterSpacing: '0.1em',
            color: C.amber, background: `${C.amber}15`, border: `1px solid ${C.amber}30`,
            borderRadius: 6, padding: '6px 16px',
          }}>
            REQUIRES PROFESSIONAL PLAN
          </div>
        </GlassPanel>
      ) : loading ? (
        <InvestorDashboardSkeleton />
      ) : error ? (
        <GlassPanel accent={C.coral}><EmptyState icon="⚠" title="Failed to load" subtitle={error.message} /></GlassPanel>
      ) : data ? (
        <InvestorDashboardView data={data} />
      ) : (
        <EmptyState icon="📊" title="Select a project" subtitle="Choose a project above to view its investor-ready dashboard." />
      )}
    </EnginePageShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SHARED DASHBOARD VIEW — used by both /investor and /share/$token
// ═══════════════════════════════════════════════════════════════════════════

export function InvestorDashboardView({ data }: { data: InvestorDashboardData }) {
  const countCompression = useCounter(Math.round(data.compressionRatio * 10) / 10, 1200, 300);
  const countEquivWeeks = useCounter(Math.round(data.totalEquivalentWeeks), 900, 500);
  const countReplCost = useCounter(data.replacementCostEstimate ? Math.round(data.replacementCostEstimate) : 0, 1000, 600);
  const countActualHours = useCounter(Math.round(data.totalActualHours), 800, 700);
  const countImpliedTeam = useCounter(data.impliedTeamSize, 600, 400);

  // Smart $ formatter — switches K → M → B by magnitude.
  const formatMoney = (dollars: number): string => {
    if (dollars >= 1_000_000_000) return `$${(dollars / 1_000_000_000).toFixed(2)}B`;
    if (dollars >= 1_000_000)     return `$${(dollars / 1_000_000).toFixed(1)}M`;
    return `$${Math.round(dollars / 1000).toLocaleString()}K`;
  };

  return (
    <div>
      {/* ══════════ HERO — THE NUMBER ══════════ */}
      <GlassPanel accent={C.amber} delay={0.15} style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        <div style={{
          display: 'flex', alignItems: 'center', padding: '40px 44px',
          background: `linear-gradient(135deg, rgba(12,22,44,0.95), rgba(5,9,16,0.98))`,
          position: 'relative',
        }}>
          {/* Radial glow behind compression number */}
          <div style={{
            position: 'absolute', left: 40, top: '50%', transform: 'translateY(-50%)',
            width: 200, height: 200,
            background: `radial-gradient(circle, ${C.amber}15 0%, transparent 60%)`,
            pointerEvents: 'none',
          }} />

          {/* Compression hero */}
          <div style={{ position: 'relative', zIndex: 1, marginRight: 44 }}>
            <div style={{ fontSize: 9, fontFamily: F.mono, letterSpacing: '0.2em', color: C.amber, marginBottom: 10 }}>
              COMPRESSION RATIO
            </div>
            <div style={{
              fontSize: 80, fontFamily: F.mono, fontWeight: 800, lineHeight: 1,
              color: C.amber,
              textShadow: `0 0 40px ${C.amberGlow}, 0 0 80px ${C.amber}20`,
            }}>
              {countCompression}×
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>
              faster than traditional development
            </div>
          </div>

          {/* Vertical divider */}
          <div style={{ width: 1, height: 100, background: `${C.faint}`, marginRight: 36, flexShrink: 0 }} />

          {/* Key value props */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px 36px', flex: 1 }}>
            {data.replacementCostEstimate != null && (
              <EngineTooltip content={<div>Market-rate cost to build this with a traditional team at $150–250/hr senior rates.</div>}>
                <div>
                  <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.15em' }}>REPLACEMENT COST</div>
                  <NeonValue color={C.neon} size={36} glow>{formatMoney(countReplCost)}</NeonValue>
                </div>
              </EngineTooltip>
            )}
            <EngineTooltip content={<div>Total equivalent engineering-weeks a traditional team would need.</div>}>
              <div>
                <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.15em' }}>EQUIV. DELIVERED</div>
                <NeonValue color={C.violet} size={32}>{countEquivWeeks}</NeonValue>
                <span style={{ fontSize: 12, fontFamily: F.mono, color: C.dim }}> weeks</span>
              </div>
            </EngineTooltip>
            <EngineTooltip content={<div>Actual hours invested by the founder + AI.</div>}>
              <div>
                <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.15em' }}>ACTUAL INVESTED</div>
                <NeonValue color={C.textSoft} size={32}>{countActualHours}</NeonValue>
                <span style={{ fontSize: 12, fontFamily: F.mono, color: C.dim }}> hours</span>
              </div>
            </EngineTooltip>
            <EngineTooltip content={<div>Number of distinct expertise domains required — each would be a different person on a traditional team.</div>}>
              <div>
                <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.15em' }}>IMPLIED TEAM SIZE</div>
                <NeonValue color={C.cyan} size={32}>{countImpliedTeam}</NeonValue>
                <span style={{ fontSize: 12, fontFamily: F.mono, color: C.dim }}> specialists</span>
              </div>
            </EngineTooltip>
          </div>
        </div>

        {/* Velocity sparkline across bottom */}
        {data.velocityTimeline.length > 0 && (
          <div style={{ height: 40, display: 'flex', alignItems: 'flex-end', gap: 1, padding: '0 44px 8px', borderTop: `1px solid ${C.faint}` }}>
            {data.velocityTimeline.map((pt, i) => {
              const maxEquiv = Math.max(...data.velocityTimeline.map(p => p.equivalentWeeks), 1);
              const h = (pt.equivalentWeeks / maxEquiv) * 100;
              return (
                <EngineTooltip key={i} content={<div style={{ fontFamily: F.mono, fontSize: 11 }}>
                  <div style={{ color: C.text, fontWeight: 600 }}>{pt.period}</div>
                  <div style={{ color: C.violet }}>{pt.equivalentWeeks.toFixed(1)} equiv wks</div>
                  <div style={{ color: C.textSoft }}>{pt.actualHours.toFixed(0)} hrs · {pt.sessionsCount} sessions</div>
                </div>}>
                  <div style={{
                    flex: 1, height: `${h}%`, minHeight: 2,
                    background: `linear-gradient(180deg, ${C.amber}60, ${C.amber}15)`,
                    borderRadius: '2px 2px 0 0',
                    opacity: 0, animation: `engine-feed-in 0.3s ease ${0.8 + i * 0.04}s forwards`,
                    cursor: 'pointer',
                  }} />
                </EngineTooltip>
              );
            })}
          </div>
        )}
      </GlassPanel>

      {/* ══════════ TWO-COLUMN: Phase Timeline + Complexity ══════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 16, marginBottom: 20 }}>
        {/* Phase Timeline */}
        <div>
          <SectionLabel color={C.cyan}>Build Timeline</SectionLabel>
          <GlassPanel accent={C.cyan} delay={0.4} style={{ padding: 0 }}>
            {data.phases.map((phase, i) => (
              <PhaseBar key={i} phase={phase} index={i} maxWeeks={Math.max(...data.phases.map(p => Math.max(p.plannedEffortWeeks, p.equivalentEffortWeeks)), 1)} />
            ))}
          </GlassPanel>
        </div>

        {/* Complexity Radar */}
        <div>
          <SectionLabel color={C.violet}>Complexity Profile</SectionLabel>
          <GlassPanel accent={C.violet} delay={0.5}>
            <ComplexityRadar complexity={data.complexity} />
            <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>OVERALL SCORE</div>
                <NeonValue color={C.violet} size={24}>{data.complexity.overallComplexityScore.toFixed(1)}</NeonValue>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>EXPERTISE DOMAINS</div>
                <NeonValue color={C.cyan} size={24}>{data.complexity.totalExpertiseDomains}</NeonValue>
              </div>
            </div>
          </GlassPanel>
        </div>
      </div>

      {/* ══════════ CAPITAL EFFICIENCY ══════════ */}
      {data.benchmarkComparison && (
        <>
          <SectionLabel color={C.neon}>Capital Efficiency vs. Industry</SectionLabel>
          <GlassPanel accent={C.neon} delay={0.6} style={{ marginBottom: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
              <EngineTooltip content={<div>Industry average cost for projects of similar scope.</div>}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>INDUSTRY COST</div>
                  <div style={{ fontSize: 22, fontFamily: F.mono, color: C.muted, textDecoration: 'line-through', opacity: 0.6 }}>
                    {formatMoney(data.benchmarkComparison.industryAvgCost)}
                  </div>
                </div>
              </EngineTooltip>
              <EngineTooltip content={<div>What it actually cost with AI-augmented development.</div>}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>YOUR COST</div>
                  <NeonValue color={C.neon} size={22}>
                    {formatMoney(data.totalActualHours * 150)}
                  </NeonValue>
                </div>
              </EngineTooltip>
              <EngineTooltip content={<div>Percentage cost savings versus traditional development.</div>}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>COST SAVINGS</div>
                  <NeonValue color={C.neon} size={22}>{data.benchmarkComparison.costSavingsPercent.toFixed(0)}%</NeonValue>
                </div>
              </EngineTooltip>
              <EngineTooltip content={<div>Percentage time savings versus industry average timeline.</div>}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>TIME SAVINGS</div>
                  <NeonValue color={C.amber} size={22}>{data.benchmarkComparison.timeSavingsPercent.toFixed(0)}%</NeonValue>
                </div>
              </EngineTooltip>
            </div>
          </GlassPanel>
        </>
      )}

      {/* ══════════ STAT ROW ══════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
        <MetricCard label="Work Items" value={`${data.completedWorkItems}/${data.totalWorkItems}`} icon="📋" color={C.cyan} tooltip={<div>Completed vs total work items.</div>} />
        <MetricCard label="Sessions" value={data.totalSessions} icon="🎯" color={C.amber} tooltip={<div>Development sessions logged.</div>} />
        {data.totalDecisions != null && <MetricCard label="Decisions" value={data.totalDecisions} icon="🧭" color={C.violet} tooltip={<div>Architectural decisions captured.</div>} />}
        {data.totalContributors != null && <MetricCard label="Contributors" value={data.totalContributors} icon="👥" color={C.neon} tooltip={<div>Human + AI contributors.</div>} />}
        <MetricCard label="Calendar Days" value={data.calendarDaysElapsed} icon="📅" color={C.blue} tooltip={<div>Days since project started.</div>} />
        <MetricCard label="$/Equiv Week" value={`$${(data.costPerEquivalentWeek / 1000).toFixed(1)}K`} icon="💰" color={C.neon}
          subtitle="industry: $15–25K"
          tooltip={<div>Cost per equivalent engineering-week. Industry benchmark is $15K–$25K at market rates.</div>} />
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 10, fontFamily: F.mono, color: C.dim }}>
        Generated {new Date(data.generatedAt).toLocaleString()} · {data.poweredBy}
      </div>
    </div>
  );
}

// ─── PhaseBar ───────────────────────────────────────────────────────────────

function PhaseBar({ phase, index, maxWeeks }: { phase: PhaseTimelineEntry; index: number; maxWeeks: number }) {
  const plannedPct = (phase.plannedEffortWeeks / maxWeeks) * 100;
  const equivPct = (phase.equivalentEffortWeeks / maxWeeks) * 100;
  const statusColor = phase.status === 'Completed' ? C.neon : phase.status === 'InProgress' ? C.cyan : C.dim;

  return (
    <EngineTooltip content={
      <div>
        <div style={{ fontWeight: 600, color: C.text, marginBottom: 6 }}>{phase.name}</div>
        <div style={{ fontSize: 11, color: C.textSoft, lineHeight: 1.6 }}>
          Planned: {phase.plannedEffortWeeks} weeks · Equivalent: {phase.equivalentEffortWeeks} weeks · Actual: {phase.actualCalendarDays} days
        </div>
        {phase.expertiseDomains.length > 0 && (
          <div style={{ marginTop: 4, fontSize: 10, color: C.violet }}>Expertise: {phase.expertiseDomains.join(', ')}</div>
        )}
      </div>
    }>
      <div style={{
        padding: '12px 18px', borderBottom: `1px solid ${C.faint}`,
        opacity: 0, animation: `engine-feed-in 0.3s ease ${0.5 + index * 0.06}s forwards`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 10, fontFamily: F.mono, fontWeight: 700, color: statusColor, background: `${statusColor}15`, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4 }}>
            {index + 1}
          </span>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{phase.name}</span>
          <StatusBadge label={phase.status} color={statusColor} />
          <span style={{ fontSize: 10, fontFamily: F.mono, color: C.dim, marginLeft: 'auto' }}>
            {phase.completedWorkItems}/{phase.workItemCount}
          </span>
        </div>
        {/* Dual bar: planned (dim) vs equivalent (bright) */}
        <div style={{ position: 'relative', height: 12, borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: C.faint, borderRadius: 4 }} />
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: `${plannedPct}%`, background: `${C.dim}40`, borderRadius: 4,
          }} />
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: `${equivPct}%`,
            background: `linear-gradient(90deg, ${C.violet}80, ${C.violet}40)`,
            borderRadius: 4,
            animation: 'engine-bar-fill 0.8s cubic-bezier(0.16,1,0.3,1)',
            boxShadow: `0 0 8px ${C.violetGlow}`,
          }} />
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 9, fontFamily: F.mono }}>
          <span style={{ color: C.dim }}>Planned: {phase.plannedEffortWeeks}w</span>
          <span style={{ color: C.violet }}>Equiv: {phase.equivalentEffortWeeks}w</span>
          <span style={{ color: C.cyan }}>Actual: {phase.actualCalendarDays}d</span>
        </div>
      </div>
    </EngineTooltip>
  );
}

// ─── ComplexityRadar (SVG) ──────────────────────────────────────────────────

function ComplexityRadar({ complexity }: { complexity: InvestorDashboardData['complexity'] }) {
  const dims = [
    { label: 'Architecture', value: complexity.avgArchitecturalDepth, max: 5, color: C.violet },
    { label: 'Integration', value: complexity.avgIntegrationPoints, max: 10, color: C.cyan },
    { label: 'Domain', value: complexity.avgDomainExpertise, max: 5, color: C.amber },
    { label: 'Infra', value: complexity.avgInfrastructureExpertise, max: 5, color: C.blue },
    { label: 'Security', value: complexity.avgSecurityExpertise, max: 5, color: C.coral },
    { label: 'Novelty', value: complexity.avgNovelty, max: 5, color: C.pink },
  ];

  const cx = 100, cy = 100, r = 70;
  const n = dims.length;

  const getPoint = (i: number, pct: number) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    return { x: cx + r * pct * Math.cos(angle), y: cy + r * pct * Math.sin(angle) };
  };

  const dataPath = dims
    .map((d, i) => getPoint(i, Math.min(d.value / d.max, 1)))
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(' ') + ' Z';

  return (
    <svg viewBox="0 0 200 200" style={{ width: '100%', maxWidth: 220, margin: '0 auto', display: 'block' }}>
      {/* Grid rings */}
      {[0.25, 0.5, 0.75, 1].map(pct => (
        <polygon
          key={pct}
          points={dims.map((_, i) => { const p = getPoint(i, pct); return `${p.x},${p.y}`; }).join(' ')}
          fill="none" stroke={C.faint} strokeWidth={0.5}
        />
      ))}
      {/* Axis lines */}
      {dims.map((_, i) => {
        const p = getPoint(i, 1);
        return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={C.faint} strokeWidth={0.5} />;
      })}
      {/* Data polygon */}
      <polygon points={dataPath.replace(/[MLZ]/g, '').trim()} fill={`${C.violet}20`} stroke={C.violet} strokeWidth={1.5} />
      {/* Data points */}
      {dims.map((d, i) => {
        const p = getPoint(i, Math.min(d.value / d.max, 1));
        return <circle key={i} cx={p.x} cy={p.y} r={3} fill={d.color} />;
      })}
      {/* Labels */}
      {dims.map((d, i) => {
        const p = getPoint(i, 1.25);
        return (
          <text key={i} x={p.x} y={p.y} textAnchor="middle" dominantBaseline="middle"
            style={{ fontSize: 7, fontFamily: F.mono, fill: d.color }}>
            {d.label}
          </text>
        );
      })}
    </svg>
  );
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function InvestorDashboardSkeleton() {
  return (
    <>
      <GlassPanel delay={0.15} style={{ marginBottom: 20, height: 200 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 40, height: '100%', padding: 40 }}>
          <div><LoadingShimmer width={200} height={60} /></div>
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <LoadingShimmer height={40} /><LoadingShimmer height={40} />
            <LoadingShimmer height={40} /><LoadingShimmer height={40} />
          </div>
        </div>
      </GlassPanel>
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 16, marginBottom: 20 }}>
        <GlassPanel delay={0.4}><LoadingShimmer height={200} /></GlassPanel>
        <GlassPanel delay={0.5}><LoadingShimmer height={200} /></GlassPanel>
      </div>
    </>
  );
}
